import { useMemo, useState } from 'react';
import { sortHand, belotePartnerOf, beloteTeamOf, type BeloteLegal, type BeloteView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME: Record<string, string> = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
const isRedSuit = (s: string) => s === 'H' || s === 'D';

export function BeloteTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as BeloteView;
  const legal = useGame((s) => s.legal) as BeloteLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? beloteTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const bidding = view.phase === 'BID1' || view.phase === 'BID2';
  const teamTricks = (team: number) => (view.tricksWon[team] ?? 0) + (view.tricksWon[team + 2] ?? 0);
  const lastBid = (s: number) => view.bids.filter((b) => b.seat === s).at(-1);
  const bidTag = (s: number) => {
    const b = lastBid(s);
    if (!bidding || !b) return null;
    return b.suit === null ? t('ta.pass') : `${t('be.take')} ${SUIT_SYMBOL[b.suit]}`;
  };

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[beloteTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === belotePartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {view.taker === s && <span className="tag">{t('be.taker')}</span>}
                {view.beloteSeat === s && <span className="tag">{t('be.belote')}</span>}
                {bidTag(s) && <span className="tag">{bidTag(s)}</span>}
                {!bidding && <span className="tag">{view.tricksWon[s] ?? 0}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('be.hand', { n: view.round })} ·{' '}
            {view.trump ? (
              <>
                {t('ta.trump')} <b style={{ color: isRedSuit(view.trump) ? 'var(--danger)' : undefined, fontSize: '1.2em' }}>{SUIT_SYMBOL[view.trump]}</b>
              </>
            ) : (
              t(view.phase === 'BID2' ? 'be.round2' : 'be.round1')
            )}
          </div>
          {bidding ? (
            <div className="slot" aria-label={t('be.upCard')} style={{ position: 'relative', display: 'grid', placeItems: 'center', gap: 4 }}>
              <PlayingCard id={view.upCard} size={trickSize} />
              <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('be.upCard')}</span>
            </div>
          ) : (
            <>
              <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
                <span className="pill team-pill mine">{t('sp.us')}: {view.teamScores[myTeam] ?? 0} · {t('wh.tricks', { n: teamTricks(myTeam) })}</span>
                <span className="pill team-pill">{t('sp.them')}: {view.teamScores[1 - myTeam] ?? 0} · {t('wh.tricks', { n: teamTricks(1 - myTeam) })}</span>
              </div>
              <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
                {view.trick.map((p, i) => {
                  const dir = seatPosition(p.seat, mySeat, 4).dir;
                  const o = OFFSETS[dir]!;
                  const off = { x: o.x * k, y: o.y * k };
                  const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
                  const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
                  return (
                    <div
                      key={p.card}
                      className="slot fly"
                      style={{
                        transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                        ['--from-x' as string]: `${off.x * 3}px`,
                        ['--from-y' as string]: `${off.y * 4}px`,
                        ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                        zIndex: i,
                      }}
                    >
                      <PlayingCard id={p.card} size={trickSize} />
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {view.phase === 'HAND_END' && view.handResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('be.handScores', { n: view.round })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.handResults.at(-1)!.points.map((p, team) => (
                  <span key={team} className={`pill ${p > 0 ? 'accent' : ''}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: +{p}
                  </span>
                ))}
              </div>
              {!view.handResults.at(-1)!.made && <div className="dim" style={{ marginTop: 4 }}>{t('be.failed', { name: name(view.handResults.at(-1)!.taker) })}</div>}
            </div>
          )}
        </div>
        <MySeat
          seat={mySeat !== null ? seats[mySeat] : undefined}
          sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts{bidding ? '' : ` · ${t('wh.tricks', { n: view.tricksWon[mySeat] ?? 0 })}`}</> : null}
        />
      </div>
      {myTurn && legal?.kind === 'bid' && <BidPicker legal={legal} upCard={view.upCard} onBid={(suit) => void act({ type: 'bid', suit })} onPass={() => void act({ type: 'pass' })} />}
      <div className="table-bottom">
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={view.tricks.length === 0 && view.trick.length === 0 ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.handResults.map((row, r) => (
              <tr key={r}>
                <td>H{r + 1}</td>
                {row.points.map((p, team) => (
                  <td key={team} className="num">
                    {p > 0 ? `+${p}` : '0'}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {SUIT_SYMBOL[row.trump]}
                      {row.beloteTeam === team ? ` · ${t('be.belote')}` : ''}
                      {row.capotTeam === team ? ` · ${t('be.capot')}` : ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('be.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}

function BidPicker({ legal, upCard, onBid, onPass }: { legal: Extract<BeloteLegal, { kind: 'bid' }>; upCard: string; onBid: (suit: 'S' | 'H' | 'D' | 'C') => void; onPass: () => void }) {
  const t = useT();
  const [sent, setSent] = useState(false);
  const send = (fn: () => void) => {
    setSent(true);
    fn();
    setTimeout(() => setSent(false), 1500);
  };
  return (
    <div className="bid-picker" role="group" aria-label={t('be.bid')}>
      <div className="bid-head">
        <strong>{t(legal.round === 1 ? 'be.takeUp' : 'be.nameTrump')}</strong>
        <span className="dim">{legal.round === 1 ? `${upCard[0] === 'T' ? '10' : upCard[0]}${SUIT_SYMBOL[upCard[1]!]}` : t('be.round2')}</span>
      </div>
      <div className="bid-chips" role="group" aria-label={t('be.chooseSuit')}>
        {legal.suits.map((s) => (
          <button
            key={s}
            className="bid-chip"
            style={{ color: isRedSuit(s) ? 'var(--danger)' : undefined, fontSize: '1.6em' }}
            disabled={sent}
            aria-label={`${t('be.take')} ${SUIT_NAME[s]}`}
            onClick={() => send(() => onBid(s as 'S' | 'H' | 'D' | 'C'))}
          >
            {SUIT_SYMBOL[s]}
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn grow" disabled={sent} onClick={() => send(onPass)}>
          {t('ta.pass')}
        </button>
      </div>
    </div>
  );
}
